import React, { useEffect, useRef, useState } from 'react';
import { Tabs, Tab, Button, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SubjectIcon from '@mui/icons-material/Subject';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { getAIScoring, getBandScore, hasTeacherReviewedScore, parseAnalytics, shouldShowScore } from './utils/scoreHelpers';
import { processSpeakingQuestionHtml } from './utils/speakingQuestionProcessor';
import './SpeakingResults.css';

const criteria = [
  ['fluency_and_coherence', 'Fluency & coherence', '#297B71'],
  ['lexical_resource', 'Lexical resource', '#7450B8'],
  ['grammatical_range_and_accuracy', 'Grammar & accuracy', '#3978A6'],
  ['pronunciation', 'Pronunciation', '#B46C35'],
];

export default function SpeakingResults({ sections = [], apiResult, preferManualScore = false, duration, durationLabel = 'Duration', teacherReviewContent }) {
  const [selectedPart, setSelectedPart] = useState(0);
  const [transcriptDialog, setTranscriptDialog] = useState(null);
  useEffect(() => { setTranscriptDialog(null); }, [apiResult]);
  const playingAudio = useRef(null);
  useEffect(() => () => { playingAudio.current?.pause(); }, [apiResult]);
  const analytics = parseAnalytics(apiResult?.response?.analytics);
  const ai = getAIScoring(apiResult);
  const scoreAvailable = shouldShowScore(apiResult);
  const band = getBandScore(apiResult, { preferManualScore });
  const teacherReviewed = preferManualScore && hasTeacherReviewedScore(apiResult);
  const parts = sections.map((section, index) => {
    const key = `part${section.order_id || index + 1}`;
    const saved = analytics[key];
    const questions = (section.questions || []).map((question, qIndex) => {
      const audioUrl = Array.isArray(saved?.questions)
        ? saved.questions.find(item => String(item.questionId) === String(question.id))?.audioUrl
        : saved?.audioUrl?.[qIndex];
      const entries = analytics.question_transcripts?.[key];
      const transcript = Array.isArray(entries) ? entries.find(item => item?.audioUrl === audioUrl)?.transcript : null;
      return { ...question, audioUrl, transcript };
    });
    return { ...section, key, questions, recorded: questions.filter(question => question.audioUrl).length };
  });
  const partIndex = selectedPart < parts.length ? selectedPart : 0;
  const part = parts[partIndex];
  const recorded = parts.reduce((total, item) => total + item.recorded, 0);
  const total = parts.reduce((sum, item) => sum + item.questions.length, 0);

  return (
    <section className="speaking-results" aria-label="IELTS Speaking results">
      <div className="sr-main">
      <header className="sr-heading">
        <div><span className="sr-eyebrow">IELTS / SPEAKING</span><h2>Your speaking results</h2></div>
        <span className="sr-status">{scoreAvailable ? (teacherReviewed ? 'Teacher reviewed' : 'Evaluation complete') : 'Pending evaluation'}</span>
      </header>

      <div className="sr-summary">
        <div className="sr-overall">
          <span>Overall band</span>
          <div><strong>{scoreAvailable && band !== null ? band : '-'}</strong><span> / 9</span></div>
          <span>{recorded} of {total} recordings</span>
        </div>
        <div className="sr-criteria">
          {criteria.map(([key, label, color]) => {
            const criterion = ai?.criteria?.[key];
            const score = typeof criterion === 'object' ? criterion?.score : criterion;
            const available = scoreAvailable && score !== null && score !== undefined && score !== '' && Number.isFinite(Number(score));
            return <div className="sr-criterion" key={key} style={{ '--criterion-color': color }}>
              <span>{label}</span><strong>{available ? score : '-'}</strong>
              <div className="sr-meter" aria-hidden="true"><i style={{ width: available ? `${Math.max(0, Math.min(9, Number(score))) / 9 * 100}%` : '0%' }} /></div>
              <small>{available ? 'AI band score' : 'Not available yet'}</small>
            </div>;
          })}
        </div>
      </div>

      <div className="sr-recordings">
      <div className="sr-section-heading"><h3>Your recordings</h3><span>{recorded} recorded / {total - recorded} not attempted</span></div>
      <Tabs value={parts.length ? partIndex : false} onChange={(_, value) => { playingAudio.current?.pause(); setSelectedPart(value); }} variant="scrollable" scrollButtons="auto" aria-label="Speaking parts" className="sr-tabs">
        {parts.map((item, index) => <Tab key={item.key} id={`sr-tab-${index}`} aria-controls={`sr-panel-${index}`} label={`Part ${item.order_id || index + 1}  (${item.recorded}/${item.questions.length})`} />)}
      </Tabs>
      {part ? <div role="tabpanel" id={`sr-panel-${partIndex}`} aria-labelledby={`sr-tab-${partIndex}`} key={part.key}>
        {part.examiner_notes && <p className="sr-part-title">{part.examiner_notes}</p>}
        {part.questions.map((question, index) => <article className="sr-question" key={`${question.id}:${question.audioUrl || ''}`}>
          <div className="sr-question-number"><span>{String(question.order_id || index + 1).padStart(2, '0')}</span><RecordVoiceOverOutlinedIcon fontSize="small" /></div>
          <div className="sr-question-body">
            <div className="sr-question-meta"><h4>Question {question.order_id || index + 1}</h4><span className={question.audioUrl ? 'sr-recorded' : 'sr-missing'}>{question.audioUrl ? 'Recorded' : 'Not attempted'}</span></div>
            {question.statement && <p>{question.statement}</p>}
            <div className="sr-question-text" dangerouslySetInnerHTML={{ __html: processSpeakingQuestionHtml(question.question_html || 'No question') }} />
            {question.cue_card_text && <blockquote className="sr-cue"><strong>Cue card</strong><p>{question.cue_card_text}</p></blockquote>}
            {question.audioUrl && <>
              <audio aria-label={`Recording for question ${question.order_id || index + 1}`} controls preload="none" src={question.audioUrl} onPlay={event => {
                if (playingAudio.current && playingAudio.current !== event.currentTarget) playingAudio.current.pause();
                playingAudio.current = event.currentTarget;
              }} />
              {typeof question.transcript === 'string' && question.transcript.trim() && <Button
                startIcon={<SubjectIcon />} sx={{ mt: 1, textTransform: 'none', color: '#7446ca' }}
                onClick={() => setTranscriptDialog({ part: part.order_id || partIndex + 1, question: question.order_id || index + 1, text: question.transcript })}
              >View transcript</Button>}
            </>}
          </div>
        </article>)}
        {!part.questions.length && <p className="sr-empty">No questions available for this part.</p>}
      </div> : <p className="sr-empty">No recordings available.</p>}
      </div>
      </div>

      <aside className="sr-sidebar" aria-label="Duration and feedback">
      {duration && <div className="sr-duration"><span className="sr-duration-icon" aria-hidden="true"><AccessTimeRoundedIcon /></span><div><span>{durationLabel}</span><strong>{duration}</strong></div></div>}
      {teacherReviewContent}
      {ai && scoreAvailable && <section className="sr-feedback" aria-label="Overall speaking feedback">
        <div className="sr-section-heading"><h3>Overall speaking feedback</h3>{ai.evaluation_confidence && <span className="sr-confidence" data-confidence={String(ai.evaluation_confidence).toLowerCase()}>AI confidence: {ai.evaluation_confidence}</span>}</div>
        {ai.reason && <p>{ai.reason}</p>}
        <div className="sr-feedback-grid">{criteria.map(([key, label, color]) => {
          const feedback = ai.criteria?.[key]?.feedback;
          const items = Array.isArray(feedback) ? feedback : typeof feedback === 'string' && feedback.trim() ? [feedback] : [];
          return <details key={key} style={{ '--criterion-color': color }}><summary>{label}</summary>{items.length ? <ul>{items.map((text, itemIndex) => <li key={itemIndex}>{text}</li>)}</ul> : <p>No specific feedback available.</p>}</details>;
        })}</div>
      </section>}
      </aside>
      <Dialog open={Boolean(transcriptDialog)} onClose={() => setTranscriptDialog(null)} fullWidth maxWidth="sm" aria-labelledby="speaking-transcript-title" PaperProps={{ sx: { borderRadius: '8px' } }}>
        <DialogTitle id="speaking-transcript-title" sx={{ pr: 7, fontSize: 18 }}>
          Part {transcriptDialog?.part}, Question {transcriptDialog?.question} transcript
          <IconButton aria-label="Close transcript" onClick={() => setTranscriptDialog(null)} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.8 }}>
          {transcriptDialog?.text}
        </DialogContent>
      </Dialog>
    </section>
  );
}
